import React from 'react';
import cx from 'classnames';
import { TeamLogoProps } from '@draftkings/component-builder';
import {
    PlayerData,
    PlayerPageConfig,
    ProductConfig,
    TrackEventFunction
} from '@draftkings/sportsbook-common-contracts';
import type { LocalizationConfig } from '@draftkings/sportsbook-labels';
import { ParsedEvent, ParsedSection } from '../../contracts';
import { headerConfig } from '../../config/headerConfig';
import './index.scss';
import { ContentWrapper } from '@draftkings/component-builder';
import { Scoreboard } from '@draftkings/scoreboards';

type MarketboardProps = {
    section: ParsedSection;
    eventsMap: ParsedEvent[];
    consumerVersion: string;
    productConfig: ProductConfig;
    marketboardRef: React.RefObject<HTMLDivElement>;
    subTabComponentId: number | undefined;
    localizationConfig: LocalizationConfig;
    locale: string;
    trackEvent: TrackEventFunction;
    playerPageConfig: (leagueId: string) => PlayerPageConfig;
    getPlayerLink: (data: PlayerData, leagueId: string, leagueName: string) => string;
    onPlayerLinkClick: (url: string, playerId: string, playerName: string) => void;
    getTeamLogo: (props: TeamLogoProps) => JSX.Element;
    onToggleExpandClick: (eventId: string) => void;
    isSingleSectionWithOneGroup?: boolean;
};

export const Marketboard: React.FC<MarketboardProps> = ({
    section,
    eventsMap,
    consumerVersion,
    marketboardRef,
    subTabComponentId,
    localizationConfig,
    locale,
    trackEvent,
    playerPageConfig,
    getPlayerLink,
    onPlayerLinkClick,
    getTeamLogo,
    onToggleExpandClick,
    isSingleSectionWithOneGroup
}) => {
    const shouldHideSeparator = isSingleSectionWithOneGroup && eventsMap.length === 1;
    const allMarketCells = eventsMap.flatMap((event) => event.parsedMarkets.marketsCells);
    const firstEvent = eventsMap[0];

    return (
        <div
            className={cx('lp-marketboard-layout', {
                'lp-marketboard-layout--no-separator': shouldHideSeparator
            })}
            key={eventsMap[0].id}
        >
            {subTabComponentId === undefined ? null : (
                <>
                    <ContentWrapper
                        componentId={subTabComponentId}
                        headerType={headerConfig[subTabComponentId]}
                        headerData={firstEvent.headerData}
                        isParlayCardComponent={firstEvent.isParlayCardComponent}
                        tags={firstEvent?.tags}
                        marketData={{
                            consumerVersion: consumerVersion,
                            marketsCells: allMarketCells,
                            widgetVersion: APP_VERSION,
                            playerPageConfig: playerPageConfig(section.id),
                            trackEvent: trackEvent,
                            getPlayerLink: (data) => getPlayerLink(data, section.id, section.name),
                            onPlayerLinkClick: onPlayerLinkClick,
                            marketboardRef,
                            getTeamLogo: getTeamLogo
                        }}
                        isExpanded={firstEvent.parsedMarkets.isExpanded}
                        contentId={firstEvent.id}
                        contentType={'event'}
                        onToggleExpandClick={() => {
                            onToggleExpandClick(firstEvent.id);
                        }}
                        animationConfig={{
                            duration: 300,
                            ease: 'ease-in-out'
                        }}
                        scoreboardComponent={
                            firstEvent.scoreboardData ? (
                                <Scoreboard
                                    {...firstEvent.scoreboardData}
                                    shouldSkipNavigation={false}
                                    getTeamLogo={getTeamLogo}
                                    locale={locale}
                                />
                            ) : undefined
                        }
                        localizationConfig={localizationConfig}
                        locale={locale}
                    />
                </>
            )}
        </div>
    );
};
